import { io } from "socket.io-client";

const ACCOUNTANT_API =
  process.env.REACT_APP_ACCOUNTANT_API;

export const CSV_EXPORT_SOCKET_EVENTS = {
  success: "csvGenerated",
  error: "csvFailed",
  accountantCsvReady: "accountant_csv_ready",
};

export const ACCOUNTANT_CSV_READY_EVENT = "accountant-csv-ready";

const csvReadyListeners = new Set();
let lastCsvReadySignature = "";
let lastCsvReadyAt = 0;

export const parseCsvSocketPayload = (raw) => {
  if (!raw) return {};
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch {
      return { raw };
    }
  }
  return raw;
};

export const subscribeAccountantCsvReady = (listener) => {
  csvReadyListeners.add(listener);
  return () => csvReadyListeners.delete(listener);
};

export const dispatchAccountantCsvReady = (payload) => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(ACCOUNTANT_CSV_READY_EVENT, { detail: payload })
  );
};

export const notifyAccountantCsvReady = (payload) => {
  const signature = JSON.stringify({
    id: payload?.csv_log_id ?? payload?._id ?? payload?.id ?? "",
    type: payload?.type ?? payload?.report_type ?? payload?.reportType ?? "",
    status: payload?.status ?? "",
    link: payload?.csv_link ?? payload?.csvLink ?? "",
  });
  const now = Date.now();
  if (signature === lastCsvReadySignature && now - lastCsvReadyAt < 1500) {
    return;
  }
  lastCsvReadySignature = signature;
  lastCsvReadyAt = now;

  dispatchAccountantCsvReady(payload);
  csvReadyListeners.forEach((listener) => {
    try {
      listener(payload);
    } catch (err) {
      console.error("[CSV Socket] listener error", err);
    }
  });
};

const unwrapCsvPayload = (payload) => {
  if (!payload || typeof payload !== "object") return payload;
  if (payload.data && typeof payload.data === "object" && !Array.isArray(payload.data)) {
    return { ...payload, ...payload.data, data: payload.data };
  }
  return payload;
};

/** CSV log / socket status: 0 pending, 1 completed, 2 failed */
export const CSV_LOG_STATUS = {
  PENDING: 0,
  COMPLETED: 1,
  FAILED: 2,
};

export const getCsvReadyStatus = (payload) => {
  const data = unwrapCsvPayload(payload);
  if (data?.status == null || data?.status === "") return null;
  const n = Number(data.status);
  return Number.isNaN(n) ? null : n;
};

export const isCsvReadyFailure = (payload) => {
  const data = unwrapCsvPayload(payload);
  const status = getCsvReadyStatus(payload);
  if (status === CSV_LOG_STATUS.FAILED || status === 3) return true;
  if (data?.success === false || payload?.success === false) return true;
  const err =
    data?.errorMessage ?? data?.message ?? payload?.errorMessage ?? payload?.error;
  return typeof err === "string" && /fail/i.test(err);
};

export const hasCsvLinkInPayload = (payload) => {
  const data = unwrapCsvPayload(payload);
  return Boolean(String(data?.csv_link ?? data?.csvLink ?? "").trim());
};

export const isCsvReadySuccess = (payload) => {
  if (isCsvReadyFailure(payload)) return false;
  const status = getCsvReadyStatus(payload);
  if (status === CSV_LOG_STATUS.PENDING) return false;
  if (status === CSV_LOG_STATUS.COMPLETED) return true;
  if (hasCsvLinkInPayload(payload)) return true;
  return false;
};

export const getCsvReadyToastMessage = (payload, { success = true } = {}) => {
  const data = unwrapCsvPayload(payload);
  const fileName = data?.file_name ?? data?.sheet_title ?? payload?.file_name;
  if (success && fileName) {
    return `CSV is Generated ${fileName}`;
  }

  const custom = data?.message ?? payload?.message;
  if (!success && typeof custom === "string" && custom.trim()) return custom.trim();

  return success
    ? "CSV is Generated"
    : "CSV export failed.";
};

export const resolveExportKey = (payload, { pendingKeys = [] } = {}) => {
  if (!payload || typeof payload !== "object") return "";
  const data = unwrapCsvPayload(payload);
  const filter =
    data?.filter && typeof data.filter === "object" ? data.filter : payload?.filter;
  const driverId =
    data?.driver_id ??
    filter?.driver_id ??
    payload?.driver_id ??
    filter?.driverId;

  const reportType = String(
    data?.report_type ??
      data?.reportType ??
      data?.type ??
      payload?.report_type ??
      payload?.type ??
      ""
  ).toLowerCase();

  if (
    reportType.includes("payment") ||
    reportType === "accountant_payment_history"
  ) {
    return "payment";
  }

  if (
    reportType.includes("ledger") ||
    reportType.includes("driver") ||
    reportType === "accountant_driver_ledger"
  ) {
    if (driverId) return `ledger:${String(driverId).trim()}`;
    const ledgerKeys = pendingKeys.filter((key) => key.startsWith("ledger:"));
    if (ledgerKeys.length === 1) return ledgerKeys[0];
  }

  if (driverId) return `ledger:${String(driverId).trim()}`;
  return "";
};

export const getSocketBaseUrl = () => {
  const explicit = process.env.REACT_APP_SOCKET_URL?.trim();
  if (explicit) return explicit.replace(/\/$/, "");
  return ACCOUNTANT_API.replace(/\/$/, "").replace(/\/v\d+$/, "");
};

/** HTTP response already contains csv_link — no socket wait */
export const hasCsvLinkInResponse = (payload) => {
  const data = payload?.data;
  if (!data || typeof data !== "object") return false;
  return Boolean(String(data.csv_link ?? data.csvLink ?? "").trim());
};

export const socketErrorMessage = (payload) => {
  if (typeof payload === "string") return payload;
  return (
    payload?.errorMessage ||
    payload?.message ||
    payload?.error ||
    "CSV export failed."
  );
};

export const socketIsSuccess = (payload) => {
  if (!payload || typeof payload !== "object") return true;
  const code = payload.code;
  if (code != null && code !== 200 && code !== 201) return false;
  return payload.success !== false && payload.status !== "failed";
};

export const createCsvExportSocket = ({ token, onSuccess, onError }) => {
  const socket = io(getSocketBaseUrl(), {
    transports: ["websocket", "polling"],
    auth: { token },
    query: { token },
  });

  socket.on(CSV_EXPORT_SOCKET_EVENTS.success, (payload) => {
    if (socketIsSuccess(payload)) onSuccess(payload);
    else onError(socketErrorMessage(payload), payload);
  });

  socket.on(CSV_EXPORT_SOCKET_EVENTS.error, (payload) => {
    onError(socketErrorMessage(payload), payload);
  });

  return socket;
};
